import { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import QRScannerCard from './components/QRScannerCard';
import NotificationFeed from './components/NotificationFeed';
import MembershipStatusCard from './components/MembershipStatusCard';
import QuickActionsCard from './components/QuickActionsCard';
import StatsOverview from './components/StatsOverview';
import { useAuth } from '../../contexts/AuthContext';
import { affiliateService } from '../../services/affiliateService';
import { notificationService } from '../../services/notificationService';
import { useNavigate } from 'react-router-dom';

const UserDashboard = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [earnings, setEarnings] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only redirect if auth is done loading AND user is not authenticated
    if (!authLoading && !user) {
      navigate('/user-login');
      return;
    }
    
    // Load dashboard data when user is ready
    if (user && !authLoading) {
      loadDashboardData();
    }
  }, [user, authLoading, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Create timeout wrapper for service calls
      const withTimeout = (promise, timeoutMs = 5000) => {
        return Promise.race([
          promise,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Service timeout')), timeoutMs)
          )
        ]);
      };

      // Load earnings and notifications with timeout protection
      const [earningsData, notificationsData] = await Promise.allSettled([
        withTimeout(affiliateService?.getEarnings(user?.id)),
        withTimeout(notificationService?.getNotifications(user?.id))
      ]);
      
      // Handle earnings result
      if (earningsData?.status === 'fulfilled') {
        setEarnings(earningsData?.value);
      } else {
        console.warn('Failed to load earnings:', earningsData?.reason);
        setEarnings({ totalEarnings: 0, totalReferrals: 0, totalPoints: 0 });
      }
      
      // Handle notifications result
      if (notificationsData?.status === 'fulfilled') {
        setNotifications(notificationsData?.value?.slice(0, 5) || []);
      } else {
        console.warn('Failed to load notifications:', notificationsData?.reason);
        setNotifications([]);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Set default values on error
      setEarnings({ totalEarnings: 0, totalReferrals: 0, totalPoints: 0 });
      setNotifications([]);
    } finally {
      // Always complete loading
      setLoading(false);
    }
  };

  const handleScanComplete = (scanData) => {
    console.log('Scan completed:', scanData);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  const userData = {
    id: user?.id,
    name: userProfile?.full_name || 'Usuario',
    email: user?.email,
    phone: userProfile?.phone || '',
    idNumber: userProfile?.id_number || '',
    type: 'user',
    membershipTier: userProfile?.membership_tier || 'Explorador',
    membershipExpiration: userProfile?.membership_expiration ? new Date(userProfile.membership_expiration) : null,
    notifications: {
      dashboard: notifications?.filter(n => !n?.isRead)?.length || 0,
      affiliate: 0
    },
    referralCode: userProfile?.referral_code || '',
    totalReferrals: earnings?.totalReferrals || 0,
    totalPoints: earnings?.totalPoints || 0,
    totalCommissions: earnings?.totalEarnings || 0
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={userData} />
      <NavigationBreadcrumbs />
      <main className="main-content pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              ¡Bienvenido, {userData?.name?.split(' ')?.[0]}!
            </h1>
            <p className="text-muted-foreground">
              Gestiona tus descuentos, escanea códigos QR y aprovecha beneficios exclusivos
            </p>
          </div>

          <div className="mb-8">
            <StatsOverview />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <QRScannerCard onScanComplete={handleScanComplete} />
              <NotificationFeed />
            </div>

            <div className="space-y-6">
              <MembershipStatusCard 
                membershipTier={userData?.membershipTier}
                expirationDate={userData?.membershipExpiration}
              />
              <QuickActionsCard />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserDashboard;